import React from "react";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { Feature } from "../../../store/types/settings";

export interface PlanFeature {
  name: string;
  cost: string;
}

export interface PlanCardProps {
  name: string;
  badge: string;
  tier: string;
  priceMonthly: string;
  priceYearly: string;
  priceQuarterly?: string;
  integration: string;
  features: string[];
  integrationServices: Array<{ name: string; cost: string }>;
  popular?: boolean;
  gradient?: string;
  isVip?: boolean;
  isCurrent?: boolean;
  isUpgrade?: boolean;
  isDowngrade?: boolean;
  buttonText?: string;
  billingType: "MONTHLY" | "QUARTERLY" | "YEARLY";
  onSelect: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  // Add-on features props
  selectedFeatures?: Feature[];
  totalPrice?: string;
  onFeatureRemove?: (featureId: string) => void;
}

const PlanCard: React.FC<PlanCardProps> = ({
  name,
  badge,
  tier,
  priceMonthly,
  priceYearly,
  priceQuarterly,
  integration,
  features,
  integrationServices,
  popular = false,
  gradient = "from-[#2196F3] to-[#1E88E5]",
  isVip = false,
  isCurrent = false,
  isUpgrade = false,
  isDowngrade = false,
  buttonText,
  billingType,
  onSelect,
  isExpanded,
  onToggleExpand,
  selectedFeatures = [],
  totalPrice,
  onFeatureRemove,
}) => {
  const isCustom = priceMonthly === "Contact Us";

  // Determine button text
  let displayButtonText = buttonText;
  if (!displayButtonText) {
    if (isCurrent) {
      displayButtonText = "Current Plan";
    } else if (isUpgrade) {
      displayButtonText = "Upgrade to This Plan";
    } else if (isDowngrade) {
      displayButtonText = "Contact Sales";
    } else {
      displayButtonText = isCustom ? "Contact Sales" : "Select Plan";
    }
  }

  return (
    <div
      className={`relative rounded-[20px] transition-all duration-300 hover:shadow-lg border mt-3 ${
        isVip
          ? "bg-gradient-to-br from-[#0B60D6] via-[#0B60D6] to-[#0B60D6] border-[#1E88E5] shadow-lg"
          : popular
            ? "bg-white border-[#2196F3] shadow-xl md:scale-105"
            : "bg-white border-[#EEEEEE] shadow-lg"
      } ${isExpanded ? "ring-1 ring-[#42A5F5]" : ""} ${
        isCurrent ? "ring-1 ring-blue-500" : ""
      }`}
    >
      {/* Popular Badge */}
      {popular && !isCurrent && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="px-4 py-1 bg-gradient-to-r from-[#1976D2] to-[#0B60D6] text-white text-[12px] font-medium rounded-full shadow-lg">
            Most Popular
          </div>
        </div>
      )}

      {/* Current Plan Badge */}
      {isCurrent && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="px-4 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[12px] font-medium rounded-full shadow-lg">
            Your Current Plan
          </div>
        </div>
      )}

      <div className="p-5">
        {/* Plan Name and Badge */}
        <div className="mb-4">
          <h3
            className={`text-2xl font-medium mb-2 ${
              isVip ? "text-white" : "text-[#212121]"
            }`}
          >
            {name}
          </h3>
          <span
            className={`inline-block px-3 py-1 text-sm font-medium rounded-full border border-[#1976D2] ${
              isVip
                ? "text-[#ffffff] bg-[#1976D2]/40"
                : "text-[#1976D2] bg-[#ffffff]/30"
            }`}
          >
            {badge}
          </span>
        </div>

        {/* Pricing */}
        <div className="mb-4">
          {isCustom ? (
            <div
              className={`text-3xl font-medium ${
                isVip ? "text-white" : "text-[#212121]"
              }`}
            >
              Contact Us
            </div>
          ) : (
            <>
              <div className="justify-between">
                <div className="flex items-center">
                  <div
                    className={`text-4xl font-medium mb-1 flex ${
                      isVip ? "text-white" : "text-[#212121]"
                    }`}
                  >
                    {billingType === "MONTHLY"
                      ? priceMonthly
                      : billingType === "QUARTERLY"
                        ? priceQuarterly
                        : priceYearly}
                  </div>
                  <div
                    className={`text-[12px] ${
                      isVip ? "text-[#90CAF9]" : "text-gray-600"
                    }`}
                  >
                    /
                    {billingType === "MONTHLY"
                      ? "month"
                      : billingType === "QUARTERLY"
                        ? "quarterly"
                        : "year"}
                  </div>
                </div>
              </div>
              <div
                className={`mt-2 text-[12px] font-medium ${
                  isVip ? "text-[#90CAF9]" : "text-[#616161]"
                }`}
              >
                Setup fee:{" "}
                <span className={isVip ? "text-[#BBDEFB]" : "text-[#000000]"}>
                  {integration}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Action Button */}
        <div className="flex items-center">
          <button
            onClick={onSelect}
            disabled={isCurrent || isDowngrade}
            className={`w-full py-3 px-6 rounded-lg transition-all flex items-center justify-center mb-4 ${
              isCurrent
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : isDowngrade
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : isCustom
                    ? isVip
                      ? "bg-white text-[#0B60D6] hover:bg-[#F5F5F5] border border-white"
                      : "bg-[#F5F5F5] text-[#212121] hover:bg-[#EEEEEE] border border-[#e2e8f0]"
                    : popular || isUpgrade
                      ? "bg-gradient-to-r from-[#1976D2] to-[#0B60D6] text-white hover:shadow-lg transform hover:scale-105"
                      : isVip
                        ? "bg-white text-[#0B60D6] hover:bg-[#F5F5F5]"
                        : "bg-[#1E88E5] text-white hover:bg-[#1976D2]"
            }`}
          >
            <span className="pr-1">{displayButtonText}</span>
            {!isCurrent && !isDowngrade && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M11.3349 10.3301V5.60547L4.47065 12.4707C4.21095 12.7304 3.78895 12.7304 3.52925 12.4707C3.26955 12.211 3.26955 11.789 3.52925 11.5293L10.3945 4.66504H5.66011C5.29284 4.66504 4.99507 4.36727 4.99507 4C4.99507 3.63273 5.29284 3.33496 5.66011 3.33496H11.9999L12.1337 3.34863C12.4369 3.41057 12.665 3.67857 12.665 4V10.3301C12.6649 10.6973 12.3672 10.9951 11.9999 10.9951C11.6327 10.9951 11.335 10.6973 11.3349 10.3301ZM11.333 4.66699L11.3349 4.66797L11.332 4.66504H11.331L11.333 4.66699Z"></path>
              </svg>
            )}
          </button>
        </div>

        {/* Features */}
        <ul className="space-y-4 mb-6">
          {features.map((feature, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <Check
                  className={`w-5 h-5 ${
                    isVip ? "text-[#ffffff]" : "text-[#616161]"
                  }`}
                />
              </div>
              <span
                className={`text-sm leading-tight ${
                  isVip ? "text-[#ffffff]" : "text-[#616161]"
                }`}
              >
                {feature}
              </span>
            </li>
          ))}
        </ul>

        {/* Selected Add-On Features */}
        {selectedFeatures && selectedFeatures.length > 0 && (
          <div className="mb-6 pt-4 border-t-2 border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Selected Add-Ons:
            </h4>
            <div className="space-y-2">
              {selectedFeatures.map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 group hover:bg-blue-100 transition-colors"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">
                      {feature.displayName}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-700">
                      {/* ₦ */}
                      {feature.price.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                    {onFeatureRemove && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onFeatureRemove(feature.id);
                        }}
                        className="p-1 hover:bg-blue-200 rounded-full transition-colors"
                        title="Remove this add-on"
                      >
                        <X className="w-4 h-4 text-blue-700" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Total Price Display */}
        {totalPrice && (
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg border-2 border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                TOTAL PRICE:
              </span>
              <span className="text-2xl font-bold text-blue-700">
                {totalPrice}
              </span>
            </div>
            {selectedFeatures && selectedFeatures.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                Base plan + {selectedFeatures.length} add-on
                {selectedFeatures.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        )}

        {/* Integration Services Toggle */}
        {onToggleExpand && (
          <button
            onClick={onToggleExpand}
            className={`w-full py-2.5 px-3 mb-4 border rounded-lg font-medium hover:transition-all flex items-center justify-center gap-2 ${
              isVip
                ? "border-[#1E88E5] text-white hover:bg-[#1976D2]"
                : "border-[#EEEEEE] text-[#616161] hover:border-[#42A5F5] hover:bg-[#E3F2FD]"
            }`}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                <span className="text-sm">Hide Integration Plan</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                <span className="text-sm">View Integration Plan</span>
              </>
            )}
          </button>
        )}

        {/* Integration Services Details */}
        {isExpanded && (
          <div
            className={`mb-6 pb-6 border-t-2 pt-6 ${
              isVip ? "border-[#1976D2]" : "border-[#F5F5F5]"
            }`}
          >
            <h4
              className={`text-sm font-medium mb-3 ${
                isVip ? "text-white" : "text-[#212121]"
              }`}
            >
              Integration Services
            </h4>
            <ul className="space-y-2">
              {integrationServices.map((service, i) => (
                <li
                  key={i}
                  className="flex items-start justify-between text-sm"
                >
                  <span className={isVip ? "text-[#BBDEFB]" : "text-[#616161]"}>
                    {service.name}
                  </span>
                  <span
                    className={`font-medium whitespace-nowrap ml-2 ${
                      isVip ? "text-[#90CAF9]" : "text-[#1E88E5]"
                    }`}
                  >
                    {service.cost}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanCard;
